import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import haukeApiService from '../services/haukeApi';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import '../assets/css/direkterzeuger-admin.css';

// Geohash-Implementierung (aus HochladeSeite übernommen)
const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

function encodeGeohash(latitude, longitude, precision = 9) {
  let idx = 0; // index into base32 map
  let bit = 0; // each char holds 5 bits
  let evenBit = true;
  let geohash = '';
  let latMin =  -90, latMax =  90;
  let lonMin = -180, lonMax = 180;

  while (geohash.length < precision) {
    if (evenBit) {
      const lonMid = (lonMin + lonMax) / 2;
      if (longitude > lonMid) {
        idx = idx * 2 + 1;
        lonMin = lonMid;
      } else {
        idx = idx * 2;
        lonMax = lonMid;
      }
    } else {
      const latMid = (latMin + latMax) / 2;
      if (latitude > latMid) {
        idx = idx * 2 + 1;
        latMin = latMid;
      } else {
        idx = idx * 2;
        latMax = latMid;
      }
    }
    evenBit = !evenBit;

    // 5 bits gives us a character
    if (bit < 4) {
      bit++;
    } else {
      geohash += BASE32[idx];
      bit = 0;
      idx = 0;
    }
  }
  return geohash;
}

const AddDirekterzeuger = ({ initialValues }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState(() => {
    // URL-Parameter für Koordinaten lesen
    const searchParams = new URLSearchParams(location.search);
    const urlLat = searchParams.get('lat');
    const urlLng = searchParams.get('lng');
    
    const data = {
      name: initialValues?.name || '',
      fullAddress: '',
      street: initialValues?.street || '',
      plz: initialValues?.plz || '',
      city: initialValues?.city || '',
      country_code: '',
      mail: '',
      mail_public: false,
      googleCoords: urlLat && urlLng ? `${urlLat}, ${urlLng}` : '',
      latitude: initialValues?.latitude ? String(initialValues.latitude) : (urlLat || ''),
      longitude: initialValues?.longitude ? String(initialValues.longitude) : (urlLng || ''),
      geohash: '',
      imageUrl: '',
      features: initialValues?.features || []
    };
    
    // Geohash automatisch berechnen falls Koordinaten vorhanden
    const hasCoords = (initialValues?.latitude && initialValues?.longitude) || (urlLat && urlLng);
    if (hasCoords) {
      const lat = parseFloat(initialValues?.latitude || urlLat);
      const lng = parseFloat(initialValues?.longitude || urlLng);
      if (!isNaN(lat) && !isNaN(lng)) {
        data.geohash = encodeGeohash(lat, lng);
      }
    }
    
    return data;
  });



  // Features nach Rubriken organisiert (aus Dienste.js übernommen)
  const featureCategories = [
    {
      title: 'Verkauf & Service',
      icon: '🛒',
      features: [
        { id: 'hofladen', label: 'Hofladen' },
        { id: 'lieferservice', label: 'Lieferservice' },
        { id: 'versand', label: 'Versand' },
        { id: 'selbstbedienung', label: 'Selbstbedienung' },
        { id: 'automat', label: 'SB-Automat' },
        { id: '24_7', label: '24/7 geöffnet' },
        { id: 'abokiste', label: 'Abokiste' },
        { id: 'online_shop', label: 'Online Shop' },
        { id: 'milchtankstelle', label: 'Milchtankstelle' },
        { id: 'selfpick', label: 'Selber Pflücken' }
      ]
    },
    {
      title: 'Märkte & Events',
      icon: '🎪',
      features: [
        { id: 'wochenmarkt', label: 'Wochenmarkt' },
        { id: 'bauernmarkt', label: 'Bauernmarkt' },
        { id: 'hofführung', label: 'Hofführung' },
        { id: 'erlebnisbauernhof', label: 'Erlebnisbauernhof' }
      ]
    },
    {
      title: 'Gastronomie',
      icon: '☕',
      features: [
        { id: 'hofcafe', label: 'Hofcafé' },
        { id: 'hofgastronomie', label: 'Hofgastronomie' }
      ]
    },
    {
      title: 'Ausstattung & Zugänglichkeit',
      icon: '♿',
      features: [
        { id: 'parken', label: 'Parkplätze vorhanden' },
        { id: 'barrierefrei', label: 'Barrierefrei' },
        { id: 'kinder_willkommen', label: 'Kinder willkommen' },
        { id: 'hunde_erlaubt', label: 'Hunde erlaubt' }
      ]
    },
    {
      title: 'Bezahlung',
      icon: '💳',
      features: [
        { id: 'payment_card', label: 'Kartenzahlung' },
        { id: 'payment_cash', label: 'Barzahlung' }
      ]
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Automatische Geohash-Berechnung wenn Latitude oder Longitude geändert wird
      if (field === 'latitude' || field === 'longitude') {
        const lat = field === 'latitude' ? parseFloat(value) : parseFloat(prev.latitude);
        const lng = field === 'longitude' ? parseFloat(value) : parseFloat(prev.longitude);
        
        if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
          newData.geohash = encodeGeohash(lat, lng);
        } else {
          newData.geohash = '';
        }
      }
      
      return newData;
    });
  };

  // Parsing für vollständige Adresse: "Am Schmidtberg 6, 40629 Düsseldorf, DE"
  const parseFullAddress = (fullAddress) => {
    if (!fullAddress.trim()) return null;

    // Pattern: street, plz city, country_code
    const pattern = /^(.+?),\s*(\d{4,5})\s+(.+?),\s*([A-Z]{2})$/;
    const match = fullAddress.trim().match(pattern);

    if (match) {
      return {
        street: match[1].trim(),
        plz: match[2].trim(),
        city: match[3].trim(),
        country_code: match[4].trim()
      };
    }

    // Alternative Pattern: street, plz city (ohne Ländercode)
    const pattern2 = /^(.+?),\s*(\d{4,5})\s+(.+?)$/;
    const match2 = fullAddress.trim().match(pattern2);

    if (match2) {
      return {
        street: match2[1].trim(),
        plz: match2[2].trim(),
        city: match2[3].trim(),
        country_code: 'DE' // Default Deutschland
      };
    }

    return null;
  };

  // Geocoding-Funktion (vereinfacht ohne API-Key)
  const geocodeAddress = async (address) => {
    try {
      // Nominatim OpenStreetMap Geocoding (kostenlos, keine API-Key nötig)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&addressdetails=1`
      );
      
      if (!response.ok) throw new Error('Geocoding fehlgeschlagen');
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        return {
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon)
        };
      }
      
      return null;
    } catch (error) {
      console.error('Geocoding Error:', error);
      return null;
    }
  };

  const handleFullAddressChange = async (value) => {
    setFormData(prev => ({ ...prev, fullAddress: value }));

    // Parse Adresse
    const parsed = parseFullAddress(value);
    if (parsed) {
      setFormData(prev => ({
        ...prev,
        street: parsed.street,
        plz: parsed.plz,
        city: parsed.city,
        country_code: parsed.country_code
      }));

      // Geocoding versuchen
      try {
        const coords = await geocodeAddress(value);
        if (coords) {
          const latitude = coords.latitude.toString().replace('.', ',');
          const longitude = coords.longitude.toString().replace('.', ',');
          const geohash = encodeGeohash(coords.latitude, coords.longitude);
          
          setFormData(prev => ({
            ...prev,
            googleCoords: `${coords.latitude}, ${coords.longitude}`,
            latitude: latitude,
            longitude: longitude,
            geohash: geohash
          }));

          showSuccessToast('Koordinaten automatisch ermittelt!');
        }
      } catch (error) {
        console.warn('Automatische Koordinaten-Ermittlung fehlgeschlagen:', error);
      }
    } else if (value.trim()) {
      // Wenn Parsing fehlschlägt, trotzdem Geocoding versuchen
      try {
        const coords = await geocodeAddress(value);
        if (coords) {
          const latitude = coords.latitude.toString().replace('.', ',');
          const longitude = coords.longitude.toString().replace('.', ',');
          const geohash = encodeGeohash(coords.latitude, coords.longitude);
          
          setFormData(prev => ({
            ...prev,
            googleCoords: `${coords.latitude}, ${coords.longitude}`,
            latitude: latitude,
            longitude: longitude,
            geohash: geohash
          }));

          showSuccessToast('Koordinaten automatisch ermittelt!');
        }
      } catch (error) {
        console.warn('Automatische Koordinaten-Ermittlung fehlgeschlagen:', error);
      }
    }
  };

  const handleGoogleCoordsChange = (value) => {
    setFormData(prev => ({ ...prev, googleCoords: value }));
    
    // Konvertiere Google Koordinaten zu latitude/longitude
    if (value.trim()) {
      const coordsPattern = /^\s*([+-]?\d+\.?\d*)\s*,\s*([+-]?\d+\.?\d*)\s*$/;
      const match = value.trim().match(coordsPattern);
      
      if (match) {
        const latitude = match[1].replace('.', ',');
        const longitude = match[2].replace('.', ',');
        const latDecimal = parseFloat(match[1]);
        const lonDecimal = parseFloat(match[2]);
        const geohash = encodeGeohash(latDecimal, lonDecimal);
        
        setFormData(prev => ({
          ...prev,
          latitude: latitude,
          longitude: longitude,
          geohash: geohash
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          latitude: '',
          longitude: '',
          geohash: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        latitude: '',
        longitude: '',
        geohash: ''
      }));
    }
  };

  const handleFeatureToggle = (featureId) => {
    const currentFeatures = formData.features || [];
    const newFeatures = currentFeatures.includes(featureId)
      ? currentFeatures.filter(f => f !== featureId)
      : [...currentFeatures, featureId];
    
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const handleSave = async () => {
    // Validierung
    if (!formData.name.trim()) {
      showErrorToast('Name ist erforderlich');
      return;
    }
    if (!formData.city.trim()) {
      showErrorToast('Stadt ist erforderlich');
      return;
    }

    try {
      setSaving(true);
      
      // Konvertiere latitude/longitude zurück zu Decimal-Format für API
      const latitudeDecimal = formData.latitude ? parseFloat(String(formData.latitude).replace(',', '.')) : 0;
      const longitudeDecimal = formData.longitude ? parseFloat(String(formData.longitude).replace(',', '.')) : 0;
      
      const newDirekterzeuger = {
        name: formData.name.trim(),
        street: formData.street.trim(),
        plz: formData.plz.trim(),
        city: formData.city.trim(),
        country_code: formData.country_code.trim() || 'DE',
        mail: formData.mail.trim(),
        mail_public: formData.mail_public && formData.mail.trim() ? formData.mail.trim() : '',
        latitude: latitudeDecimal,
        longitude: longitudeDecimal,
        geohash: latitudeDecimal && longitudeDecimal ? encodeGeohash(latitudeDecimal, longitudeDecimal) : '',
        imageUrl: formData.imageUrl.trim() ? [formData.imageUrl.trim()] : [],
        features: formData.features,
        membership: 'basic'
      };

      console.log('Erstelle neuen Direkterzeuger:', newDirekterzeuger);
      
      const response = await haukeApiService.createDirekterzeuger(newDirekterzeuger);
      
      showSuccessToast('Direkterzeuger wurde erfolgreich angelegt!');
      navigate('/direkterzeuger-admin');
      
    } catch (error) {
      console.error('Fehler beim Anlegen:', error);
      showErrorToast(`Fehler beim Anlegen: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/direkterzeuger-admin');
  };

  return (
    <div className="mantis-products">
      <div className="filters-section">
        <div className="search-form-container">
          <div className="search-form-header">
            <h3>Neuen Direkterzeuger anlegen</h3>
            <div className="search-form-actions">
              <button 
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                <i className="material-icons">save</i>
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
              <button 
                className="btn btn-outline"
                onClick={handleCancel}
                disabled={saving}
              >
                <i className="material-icons">arrow_back</i>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="products-content">
        <div className="direkterzeuger-form">
          
          {/* Grunddaten */}
          <div className="form-section">
            <h4>Grunddaten</h4>
            <div className="search-form-grid">
              <div className="search-field">
                <label>Name *</label>
                <input
                  type="text"
                  placeholder="Name des Direkterzeugers"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Vollständige Adresse */}
          <div className="form-section">
            <h4>Vollständige Adresse</h4>
            <div className="search-form-grid">
              <div className="search-field" style={{gridColumn: '1 / -1'}}>
                <label>Adresse komplett</label>
                <input
                  type="text"
                  placeholder="z.B. Am Schmidtberg 6, 40629 Düsseldorf, DE"
                  value={formData.fullAddress}
                  onChange={(e) => handleFullAddressChange(e.target.value)}
                />
                <small>Format: Straße Nr, PLZ Stadt, Ländercode (automatische Aufteilung und Koordinaten-Ermittlung)</small>
              </div>
            </div>
          </div>

          {/* Adresse */}
          <div className="form-section">
            <h4>Adresse (Einzelfelder)</h4>
            <div className="search-form-grid">
              <div className="search-field">
                <label>Straße</label>
                <input
                  type="text"
                  placeholder="Straße und Hausnummer (optional)"
                  value={formData.street}
                  onChange={(e) => handleInputChange('street', e.target.value)}
                />
              </div>
              <div className="search-field">
                <label>PLZ</label>
                <input
                  type="text"
                  placeholder="PLZ (optional)"
                  value={formData.plz}
                  onChange={(e) => handleInputChange('plz', e.target.value)}
                />
              </div>
              <div className="search-field">
                <label>Stadt *</label>
                <input
                  type="text"
                  placeholder="Stadt"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
              </div>
              <div className="search-field">
                <label>Ländercode</label>
                <input
                  type="text"
                  placeholder="DE"
                  value={formData.country_code}
                  onChange={(e) => handleInputChange('country_code', e.target.value)}
                  maxLength="2"
                  style={{textTransform: 'uppercase'}}
                />
              </div>
            </div>
          </div>

          {/* Kontakt */}
          <div className="form-section">
            <h4>Kontakt</h4>
            <div className="search-form-grid">
              <div className="search-field">
                <label>E-Mail</label>
                <input
                  type="email"
                  placeholder="E-Mail-Adresse (optional)"
                  value={formData.mail}
                  onChange={(e) => handleInputChange('mail', e.target.value)}
                />
              </div>
              <div className="search-field">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.mail_public}
                    onChange={(e) => handleInputChange('mail_public', e.target.checked)}
                  />
                  E-Mail öffentlich anzeigen
                </label>
              </div>
            </div>
          </div>

          {/* Koordinaten */}
          <div className="form-section">
            <h4>Standort</h4>
            {new URLSearchParams(location.search).get('lat') && (
              <div style={{ padding: '8px 12px', background: '#E8F5E9', borderRadius: '4px', marginBottom: '16px', fontSize: '14px', color: '#2E7D32' }}>
                ✓ Koordinaten aus Kartenauswahl übernommen
              </div>
            )}
            <div className="search-form-grid">
              <div className="search-field">
                <label>Google Koordinaten</label>
                <input
                  type="text"
                  placeholder="z.B. 51.43242393928278, 7.231362481906907"
                  value={formData.googleCoords}
                  onChange={(e) => handleGoogleCoordsChange(e.target.value)}
                />
                <small>Kopieren Sie die Koordinaten aus Google Maps</small>
              </div>
              <div className="search-field">
                <label>Latitude</label>
                <input
                  type="text"
                  placeholder="Automatisch berechnet"
                  value={formData.latitude}
                  readOnly
                />
              </div>
              <div className="search-field">
                <label>Longitude</label>
                <input
                  type="text"
                  placeholder="Automatisch berechnet"
                  value={formData.longitude}
                  readOnly
                />
              </div>
              <div className="search-field">
                <label>Geohash</label>
                <input
                  type="text"
                  placeholder="Automatisch berechnet"
                  value={formData.geohash}
                  readOnly
                />
                <small>9-stelliger Geohash für Standort-Indizierung</small>
              </div>
            </div>
          </div>

          {/* Image URL */}
          <div className="form-section">
            <h4>Bild URL (optional)</h4>
            <div className="search-field">
              <label>Bild URL</label>
              <input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={formData.imageUrl}
                onChange={(e) => handleInputChange('imageUrl', e.target.value)}
              />
              <small>URL zu einem Bild des Direkterzeugers</small>
            </div>
          </div>

          {/* Bild */}
          <div className="form-section">
            <h4>Bild</h4>
            <div className="search-form-grid">
              <div className="search-field">
                <label>Bild-URL</label>
                <input
                  type="url"
                  placeholder="https://example.com/bild.jpg"
                  value={formData.imageUrl}
                  onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="form-section">
            <h4>Dienste & Features</h4>
            <div className="features-container">
              {featureCategories.map(category => (
                <div key={category.title} className="feature-category">
                  <h5>
                    <span className="category-icon">{category.icon}</span>
                    {category.title}
                  </h5>
                  <div className="features-grid">
                    {category.features.map(feature => (
                      <label key={feature.id} className="feature-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.features.includes(feature.id)}
                          onChange={() => handleFeatureToggle(feature.id)}
                          disabled={saving}
                        />
                        <span className="checkbox-label">{feature.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AddDirekterzeuger;